import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ChevronRight } from 'lucide-react'
import { artistFetch } from '@/lib/artist-api'
import { PageHeader } from '@/components/PageHeader'

const PERMISSION_LABELS: Record<string, string> = {
  view_shows: 'View Shows',
  view_setlists: 'View Setlists',
  submit_debriefs: 'Submit Debriefs',
  view_travel: 'View Travel',
  view_logistics: 'View Logistics',
  view_merch: 'View Merch',
}

const DEPT_LABELS: Record<string, string> = {
  artist_and_music: 'Artist & Music',
  management_and_ops: 'Management & Ops',
  sound: 'Sound',
  lighting_and_visuals: 'Lighting & Visuals',
  stage_and_backline: 'Stage & Backline',
  wardrobe_and_personal: 'Wardrobe & Personal',
  local_production: 'Local Production',
  venue_staff: 'Venue Staff',
}

type View =
  | { level: 'team' }
  | { level: 'department'; department: string }
  | { level: 'role'; department: string; role: string }
  | { level: 'individual'; department: string; role: string; crewProfileId: string }

export function TeamPermissionsPage() {
  const [view, setView] = useState<View>({ level: 'team' })

  const breadcrumbs: { label: string; onClick: () => void }[] = [
    { label: 'Team', onClick: () => setView({ level: 'team' }) },
  ]
  if (view.level !== 'team') {
    breadcrumbs.push({
      label: DEPT_LABELS[view.department] ?? view.department,
      onClick: () => setView({ level: 'department', department: view.department }),
    })
  }
  if (view.level === 'role' || view.level === 'individual') {
    breadcrumbs.push({
      label: view.role.replace(/_/g, ' '),
      onClick: () =>
        setView({ level: 'role', department: view.department, role: view.role }),
    })
  }
  if (view.level === 'individual') {
    breadcrumbs.push({
      label: 'Individual',
      onClick: () => {},
    })
  }

  return (
    <div>
      <PageHeader title="Team Permissions" subtitle="Manage crew access to platform features" />

      {/* Breadcrumbs */}
      <nav className="mb-6 flex items-center gap-1 text-sm">
        {breadcrumbs.map((bc, i) => (
          <span key={i} className="flex items-center gap-1">
            {i > 0 && <ChevronRight className="h-3 w-3 text-ovation-muted" />}
            <button
              type="button"
              onClick={bc.onClick}
              className={`rounded px-1.5 py-0.5 ${
                i === breadcrumbs.length - 1
                  ? 'font-medium text-ovation-ink'
                  : 'text-ovation-muted hover:text-ovation-ink'
              }`}
            >
              {bc.label}
            </button>
          </span>
        ))}
      </nav>

      {view.level === 'team' && <TeamView onSelect={(d) => setView({ level: 'department', department: d })} />}
      {view.level === 'department' && (
        <DepartmentView
          department={view.department}
          onSelectRole={(r) => setView({ level: 'role', department: view.department, role: r })}
        />
      )}
      {view.level === 'role' && (
        <RoleView
          department={view.department}
          role={view.role}
          onSelectIndividual={(id) =>
            setView({ level: 'individual', department: view.department, role: view.role, crewProfileId: id })
          }
        />
      )}
      {view.level === 'individual' && (
        <IndividualView department={view.department} role={view.role} crewProfileId={view.crewProfileId} />
      )}
    </div>
  )
}

// -- Team View ---------------------------------------------------------------

function TeamView({ onSelect }: { onSelect: (dept: string) => void }) {
  const { data, isLoading } = useQuery<Array<{
    department: string; memberCount: number; enabledCount: number
    permissions: Record<string, boolean>
  }>>({
    queryKey: ['team-permissions', 'departments'],
    queryFn: () => artistFetch('/team-permissions/departments'),
  })

  if (isLoading) return <LoadingSkeleton />

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {(data ?? []).map((dept) => (
        <button
          key={dept.department}
          type="button"
          onClick={() => onSelect(dept.department)}
          className="flex items-center justify-between rounded-lg border border-ovation-ink/10 bg-white p-4 text-left transition-colors hover:border-ovation-ink/25"
        >
          <div>
            <div className="font-medium text-ovation-ink">
              {DEPT_LABELS[dept.department] ?? dept.department}
            </div>
            <div className="mt-1 text-xs text-ovation-muted">
              {dept.memberCount} member{dept.memberCount !== 1 ? 's' : ''} · {dept.enabledCount} permissions
            </div>
          </div>
          <ChevronRight className="h-4 w-4 text-ovation-muted" />
        </button>
      ))}
    </div>
  )
}

// -- Department View ---------------------------------------------------------

function DepartmentView({
  department,
  onSelectRole,
}: {
  department: string
  onSelectRole: (role: string) => void
}) {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery<{
    permissions: Record<string, boolean>
    roles: Array<{ role: string; memberCount: number; hasOverrides: boolean; permissions: Record<string, string> }>
  }>({
    queryKey: ['team-permissions', 'departments', department],
    queryFn: () => artistFetch(`/team-permissions/departments/${department}`),
  })

  const mutation = useMutation({
    mutationFn: (perms: { permission: string; enabled: boolean }[]) =>
      artistFetch(`/team-permissions/departments/${department}`, {
        method: 'PATCH',
        body: { permissions: perms },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team-permissions'] })
    },
  })

  if (isLoading || !data) return <LoadingSkeleton />

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-ovation-ink/10 bg-white p-4">
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-ovation-muted">
          Department Defaults
        </h3>
        <div className="space-y-2">
          {Object.entries(PERMISSION_LABELS).map(([key, label]) => (
            <label key={key} className="flex items-center justify-between">
              <span className="text-sm text-ovation-ink">{label}</span>
              <input
                type="checkbox"
                checked={data.permissions[key] ?? false}
                onChange={(e) =>
                  mutation.mutate([{ permission: key, enabled: e.target.checked }])
                }
                className="h-4 w-4 rounded border-ovation-ink/20"
              />
            </label>
          ))}
        </div>
      </div>

      <div>
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-ovation-muted">
          Roles
        </h3>
        <div className="space-y-2">
          {data.roles.map((role) => (
            <button
              key={role.role}
              type="button"
              onClick={() => onSelectRole(role.role)}
              className="flex w-full items-center justify-between rounded-lg border border-ovation-ink/10 bg-white p-3 text-left transition-colors hover:border-ovation-ink/25"
            >
              <div>
                <div className="text-sm font-medium text-ovation-ink">
                  {role.role.replace(/_/g, ' ')}
                </div>
                <div className="text-xs text-ovation-muted">
                  {role.memberCount} member{role.memberCount !== 1 ? 's' : ''}
                  {role.hasOverrides && ' · has overrides'}
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-ovation-muted" />
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

// -- Role View ---------------------------------------------------------------

function RoleView({
  department,
  role,
  onSelectIndividual,
}: {
  department: string
  role: string
  onSelectIndividual: (id: string) => void
}) {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery<{
    permissions: Record<string, string>
    effective: Record<string, boolean>
    individuals: Array<{
      crewProfileId: string; displayName: string; role: string; hasOverrides: boolean
      permissions: Record<string, string>; effective: Record<string, boolean>
    }>
  }>({
    queryKey: ['team-permissions', 'departments', department, 'roles', role],
    queryFn: () => artistFetch(`/team-permissions/departments/${department}/roles/${role}`),
  })

  const mutation = useMutation({
    mutationFn: (perms: { permission: string; state: string }[]) =>
      artistFetch(`/team-permissions/departments/${department}/roles/${role}`, {
        method: 'PATCH',
        body: { permissions: perms },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team-permissions'] })
    },
  })

  if (isLoading || !data) return <LoadingSkeleton />

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-ovation-ink/10 bg-white p-4">
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-ovation-muted">
          Role Overrides
        </h3>
        <OverrideToggles
          permissions={data.permissions}
          effective={data.effective}
          onChange={(perm, state) => mutation.mutate([{ permission: perm, state }])}
        />
      </div>

      <div>
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-ovation-muted">
          Individuals
        </h3>
        <div className="space-y-2">
          {data.individuals.map((ind) => (
            <button
              key={ind.crewProfileId}
              type="button"
              onClick={() => onSelectIndividual(ind.crewProfileId)}
              className="flex w-full items-center justify-between rounded-lg border border-ovation-ink/10 bg-white p-3 text-left transition-colors hover:border-ovation-ink/25"
            >
              <div>
                <div className="text-sm font-medium text-ovation-ink">{ind.displayName}</div>
                <div className="text-xs text-ovation-muted">
                  {ind.role.replace(/_/g, ' ')}
                  {ind.hasOverrides && ' · has overrides'}
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-ovation-muted" />
            </button>
          ))}
          {data.individuals.length === 0 && (
            <div className="rounded-md border border-ovation-ink/10 bg-white p-4 text-sm text-ovation-muted">
              No crew members currently engaged in this role.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// -- Individual View ---------------------------------------------------------

function IndividualView({
  department,
  role,
  crewProfileId,
}: {
  department: string
  role: string
  crewProfileId: string
}) {
  const qc = useQueryClient()
  const { data, isLoading } = useQuery<{
    crewProfileId: string; displayName: string; role: string
    permissions: Record<string, string>; effective: Record<string, boolean>
  }>({
    queryKey: ['team-permissions', 'crew', crewProfileId],
    queryFn: () => artistFetch(`/team-permissions/crew/${crewProfileId}`),
  })

  const mutation = useMutation({
    mutationFn: (perms: { permission: string; state: string }[]) =>
      artistFetch(`/team-permissions/crew/${crewProfileId}`, {
        method: 'PATCH',
        body: { permissions: perms },
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['team-permissions'] })
    },
  })

  if (isLoading || !data) return <LoadingSkeleton />

  // Suppress unused variable warnings — these are used in breadcrumb context
  void department
  void role

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-ovation-ink/10 bg-white p-4">
        <div className="mb-3">
          <div className="font-medium text-ovation-ink">{data.displayName}</div>
          <div className="text-xs text-ovation-muted">{data.role.replace(/_/g, ' ')}</div>
        </div>
        <OverrideToggles
          permissions={data.permissions}
          effective={data.effective}
          onChange={(perm, state) => mutation.mutate([{ permission: perm, state }])}
        />
      </div>
    </div>
  )
}

// -- Shared Components -------------------------------------------------------

function OverrideToggles({
  permissions,
  effective,
  onChange,
}: {
  permissions: Record<string, string>
  effective: Record<string, boolean>
  onChange: (perm: string, state: 'inherit' | 'enable' | 'disable') => void
}) {
  return (
    <div className="space-y-3">
      {Object.entries(PERMISSION_LABELS).map(([key, label]) => {
        const state = permissions[key] ?? 'inherit'
        const resolved = effective[key] ?? false
        return (
          <div key={key} className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm text-ovation-ink">{label}</span>
              <span
                className={`rounded px-1.5 py-0.5 text-[10px] uppercase ${
                  resolved
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-rose-100 text-rose-700'
                }`}
              >
                {resolved ? 'on' : 'off'}
              </span>
            </div>
            <select
              value={state}
              onChange={(e) => onChange(key, e.target.value as 'inherit' | 'enable' | 'disable')}
              className="rounded-md border border-ovation-ink/15 px-2 py-1 text-xs"
            >
              <option value="inherit">Inherit</option>
              <option value="enable">Enable</option>
              <option value="disable">Disable</option>
            </select>
          </div>
        )
      })}
    </div>
  )
}

function LoadingSkeleton() {
  return (
    <div className="flex items-center justify-center py-12">
      <div className="h-6 w-6 animate-spin rounded-full border-2 border-ovation-ink/20 border-t-ovation-ink" />
    </div>
  )
}
